export interface FormattedDocumentError {
  code: string
  message: string
  link?: {
    label: string
    url: string
  }
}

export const DOCUMENT_ERROR_MESSAGES: Record<string, string> = {
  INSUFFICIENT_STOCK:
    'Không sửa/hủy được vì hàng đã xuất bớt. Hãy làm phiếu điều chỉnh tồn hoặc hoàn lại phiếu xuất trước.',
  OVER_RECEIPT: 'Số nhận vượt số còn lại của dòng PO.',
  BASE_UOM_LOCKED: 'Vật tư đã phát sinh giao dịch, không đổi được đơn vị gốc.',
  UOM_FACTOR_MISSING: 'Thiếu hệ số quy đổi; khai báo ở trang chi tiết vật tư.',
  CANNOT_MODIFY_RECEIVED_LINE:
    'Không thể đổi vật tư, đơn vị hoặc kích thước của dòng đã nhận/thiếu hàng.',
  CANNOT_CHANGE_SUPPLIER_WITH_RECEIPTS:
    'Không thể đổi nhà cung cấp vì PO đã có phiếu nhập kho.',
  ORDER_QTY_BELOW_RECEIVED:
    'Số lượng đặt không thể nhỏ hơn tổng số lượng đã nhận và thiếu.',
  CANNOT_DELETE_RECEIVED_LINE:
    'Không thể xóa dòng hàng đã nhận hoặc ghi nhận thiếu.',
  INVALID_PRICE: 'Đơn giá không hợp lệ. Đơn giá phải lớn hơn 0.',
  CANCELLED_PO_LOCKED:
    'Đơn hàng đã bị hủy, chỉ có thể khôi phục hoặc sửa ghi chú/ngày giao.',
  RECEIPT_ALREADY_VOID: 'Chứng từ này đã bị hủy trước đó.',
  STOCK_ISSUE_ALREADY_VOID: 'Chứng từ này đã bị hủy trước đó.',
  RETURN_ALREADY_VOID: 'Chứng từ này đã bị hủy trước đó.',
  INBOUND_ALREADY_VOID: 'Chứng từ này đã bị hủy trước đó.',
  INVALID_SOURCE: 'Nguồn nhập kho không hợp lệ.',
  EMPTY_LINES: 'Phiếu nhập kho phải có ít nhất một dòng vật tư.',
  BASE_UOM_MISSING: 'Vật tư chưa được cấu hình đơn vị gốc.',
  INVALID_STATE: 'Trạng thái đơn hàng không hợp lệ cho thao tác này.',
  ORDER_ALREADY_CANCELLED: 'Đơn hàng này đã bị hủy trước đó.',
  ORDER_NOT_CANCELLED: 'Chỉ có thể khôi phục đơn hàng đang ở trạng thái đã hủy.',
  LINE_NOT_SX: 'Dòng đơn hàng phải ở trạng thái Sản xuất (sx) mới được đưa vào nhu cầu.',
  ORDER_CANCELLED: 'Đơn hàng đã bị hủy, không thể chỉnh sửa dòng hàng hoặc ngày giao.',
  LINE_CANCELLED: 'Dòng đơn hàng đã bị hủy không thể chỉnh sửa.',
  LINE_NOT_DELETABLE: 'Chỉ có thể xóa dòng ở trạng thái Nháp. Dòng đang Sản xuất hoặc Đã hủy không được xóa.',
  INVALID_TRANSITION: 'Chuyển trạng thái dòng đơn hàng không hợp lệ.',
  SX_REQUIREMENTS_MISSING: 'Dòng đơn hàng chưa đủ điều kiện để chuyển sang Sản xuất.',
  LINE_IN_DEMAND: 'Không thể xóa dòng đơn hàng đã được đưa vào đợt nhu cầu sản xuất.',
  LINE_ALREADY_IN_DEMAND: 'Dòng đơn hàng này đã được đưa vào một đợt nhu cầu trước đó.',
  INACTIVE_CUSTOMER: 'Khách hàng đang bị vô hiệu hóa, không thể tạo hoặc cập nhật đơn hàng.',
  INVALID_BUYER: 'Người mua không hợp lệ hoặc không thuộc khách hàng đã chọn.',
  INVALID_DATES: 'Ngày yêu cầu giao hàng không được sớm hơn ngày đặt hàng.',
  INACTIVE_OR_INVALID_VARIANT: 'Variant sản phẩm không tồn tại hoặc đang bị vô hiệu hóa.',
  INACTIVE_VARIANT: 'Variant sản phẩm không còn hoạt động, không thể đưa vào nhu cầu.',
  CUSTOMER_HAS_OPEN_ORDERS: 'Không thể ngừng dùng khách hàng khi còn đơn hàng chưa hoàn tất.',
  BATCH_ALREADY_EXPLODED: 'Đợt nhu cầu này đã chạy bóc tách (explosion), không thể thêm dòng mới.',
  VALIDATION_FAILED: 'Dữ liệu nhập vào không hợp lệ hoặc thiếu thông tin bắt buộc.',
  IN_USE:
    'Đơn vị đang được dùng trong Lệnh sản xuất. Hãy chọn Ngừng dùng thay vì xóa.',
  DUPLICATE_WORK_UNIT_CODE: 'Mã đơn vị sản xuất đã tồn tại.',
  WORK_UNIT_KIND_MISMATCH: 'Loại đơn vị không khớp (mộc/sơn/gia công).',
  MILESTONE_ORDER:
    'Thứ tự mốc ngày không hợp lệ (NVL về ≤ xong mộc trắng ≤ xong sơn ≤ hoàn thành nhà máy ≤ giao).',
  MONTH_LOCKED: 'Tháng đã khóa, hãy Mở lại (ghi lý do) để sửa.',
}

export function formatDocumentError(
  error: unknown,
  materialId?: string,
): FormattedDocumentError {
  if (!error) {
    return { code: 'UNKNOWN', message: 'Đã có lỗi không xác định xảy ra.' }
  }

  const errObj = error as { code?: string; message?: string; details?: Record<string, unknown> }
  const code = errObj.code || 'UNKNOWN'
  const effectiveMaterialId = materialId || (errObj.details?.material_id as string | undefined)

  if (code in DOCUMENT_ERROR_MESSAGES) {
    const hasRichVietnameseMsg =
      errObj.message &&
      /[\u00C0-\u1EF9]/.test(errObj.message) &&
      errObj.message !== DOCUMENT_ERROR_MESSAGES[code]

    const formatted: FormattedDocumentError = {
      code,
      message: hasRichVietnameseMsg ? errObj.message! : DOCUMENT_ERROR_MESSAGES[code],
    }

    if (code === 'UOM_FACTOR_MISSING' || code === 'BASE_UOM_MISSING') {
      formatted.link = {
        label: 'Xem chi tiết vật tư',
        url: effectiveMaterialId ? `/purchasing/materials/${effectiveMaterialId}` : '/purchasing/materials',
      }
    }

    return formatted
  }

  const rawMessage =
    errObj.message || (error instanceof Error ? error.message : String(error))

  const formatted: FormattedDocumentError = {
    code,
    message: rawMessage || 'Thao tác không thành công. Vui lòng kiểm tra lại.',
  }

  if (effectiveMaterialId) {
    formatted.link = {
      label: 'Xem chi tiết vật tư',
      url: `/purchasing/materials/${effectiveMaterialId}`,
    }
  }

  return formatted
}
